import { CALIBRATION, CAPTURE } from '@/config/audio';
import type {
  AnalysisFrame,
  AudioEngineState,
  AudioErrorCode,
  CalibrationQuality,
  CalibrationResult,
  MicStatus,
  OnsetEvent,
  PitchReading,
  StringReport,
} from '@/types/audio';
import AnalysisWorker from './analysis.worker?worker';
import { toNoiseProfile, type FromWorker, type SerializableFrame, type ToWorker } from './messages';

const WORKLET_URL = '/worklets/capture-processor.js';

/** How often state changes are pushed to React, in ms. */
const STATE_EMIT_INTERVAL_MS = 100;

/** Exponential smoothing factor for the displayed input level. */
const LEVEL_SMOOTHING = 0.25;

/**
 * How long the microphone stays open after the last screen that needed it goes
 * away. Long enough to walk from setup into a session, or to finish one
 * exercise and start another, without paying for a fresh permission check and
 * recalibration; short enough that browsing the chord library does not leave the
 * recording indicator burning.
 */
const IDLE_RELEASE_MS = 30_000;

export interface EngineFrame extends AnalysisFrame {
  bass?: { expectedDb: number; unwantedDb: number };
  strings?: StringReport[];
}

type Unsubscribe = () => void;

/**
 * Owns the microphone, the AudioContext, the capture worklet and the analysis
 * worker, and nothing else. It knows nothing about chords, training or React.
 *
 * Consumers come in two flavours:
 *
 * - React subscribes to `subscribe`/`getSnapshot` and gets a **throttled**
 *   snapshot at 10 Hz, because status and level are all it needs;
 * - training engines subscribe to `onFrame`/`onOnset` and get **every** event
 *   at full rate, outside the React lifecycle entirely.
 */
export class AudioEngine {
  private state: AudioEngineState = {
    status: 'idle',
    level: 0,
    frame: null,
    contextSampleRate: 0,
  };

  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private worker: Worker | null = null;
  private silentSink: GainNode | null = null;

  private readonly stateListeners = new Set<() => void>();
  private readonly frameListeners = new Set<(frame: EngineFrame) => void>();
  private readonly onsetListeners = new Set<(onset: OnsetEvent) => void>();
  private readonly pitchListeners = new Set<(pitch: PitchReading) => void>();

  private lastEmit = 0;
  private emitTimer: ReturnType<typeof setTimeout> | null = null;
  private startPromise: Promise<void> | null = null;
  private calibrationSettle: ((result: CalibrationResult | null) => void) | null = null;

  /**
   * Incremented on every teardown. Async work captures the value it started
   * with and drops its result if the engine has been torn down since — the
   * alternative is a calibration from a dead stream landing on a live one.
   */
  private generation = 0;

  /** Screens currently needing the microphone; see `acquire()`. */
  private holders = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Recovery from a suspension belongs to the engine, not to whichever page
    // happens to register a lifecycle hook: a setup screen or the clean-chord
    // trainer would otherwise come back from an iOS screen lock with a live
    // meter reading nothing. Pages still register their own handlers to pause
    // their exercise; this only brings the context back.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) void this.resume();
      });
    }
  }

  // ── React-facing store ────────────────────────────────────────────────

  subscribe = (listener: () => void): Unsubscribe => {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  };

  getSnapshot = (): AudioEngineState => this.state;

  // ── Engine-facing streams (full rate, no React) ───────────────────────

  onFrame(listener: (frame: EngineFrame) => void): Unsubscribe {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  onOnset(listener: (onset: OnsetEvent) => void): Unsubscribe {
    this.onsetListeners.add(listener);
    return () => this.onsetListeners.delete(listener);
  }

  onPitch(listener: (pitch: PitchReading) => void): Unsubscribe {
    this.pitchListeners.add(listener);
    return () => this.pitchListeners.delete(listener);
  }

  get audioContext(): AudioContext | null {
    return this.context;
  }

  get isReady(): boolean {
    return this.state.status === 'ready';
  }

  /**
   * Register interest in the microphone. Returns a release function.
   *
   * Every screen that analyses audio holds one of these for as long as it is
   * mounted. When the last holder lets go the microphone is shut down after a
   * short grace period, so the browser's recording indicator does not stay lit
   * while someone reads the chord library — and a phone is not running FFTs in
   * the background for the rest of the session.
   */
  acquire(): Unsubscribe {
    this.holders++;
    if (this.releaseTimer !== null) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holders = Math.max(0, this.holders - 1);
      if (this.holders > 0) return;
      if (this.releaseTimer !== null) clearTimeout(this.releaseTimer);
      this.releaseTimer = setTimeout(() => {
        this.releaseTimer = null;
        if (this.holders === 0) this.stop();
      }, IDLE_RELEASE_MS);
    };
  }

  /**
   * Request the microphone, start the pipeline, and calibrate.
   *
   * **Must be called synchronously from a user gesture.** Safari does not treat
   * the user's answer to the permission prompt as a gesture, so an AudioContext
   * created after `await getUserMedia()` can stay suspended forever. The context
   * is therefore created and resumed here, before anything is awaited.
   */
  start(): Promise<void> {
    if (this.state.status === 'ready') {
      return this.resume();
    }
    if (this.startPromise) return this.startPromise;

    const unsupported = detectUnsupported();
    if (unsupported) {
      this.fail(unsupported);
      return Promise.resolve();
    }

    // Safari implements the Audio Session API; 'play-and-record' tells iOS the
    // page both captures and plays, which keeps the audio route sane while the
    // microphone is live. Everywhere else the property simply does not exist.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) {
      try {
        session.type = 'play-and-record';
      } catch {
        // A draft API is allowed to reject values; the default routing stands.
      }
    }

    // Synchronous, inside the gesture: create the context and kick off resume.
    this.teardown();
    const context = new AudioContext({ latencyHint: 'interactive' });
    this.context = context;
    void context.resume().catch(() => {
      // Reported later if the context is still not running after setup.
    });

    this.startPromise = this.doStart(context, this.generation).finally(() => {
      this.startPromise = null;
    });
    return this.startPromise;
  }

  private async doStart(context: AudioContext, generation: number): Promise<void> {
    this.setState({ status: 'requesting', errorCode: undefined });

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: CAPTURE.constraints,
        video: false,
      });
    } catch (error) {
      if (generation !== this.generation) return;
      this.fail(mapGetUserMediaError(error));
      void context.close().catch(() => {});
      this.context = null;
      return;
    }

    // The user may have navigated away, or stop() may have run, while the
    // permission prompt was open. Do not adopt a stream nobody asked for.
    if (generation !== this.generation) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.stream = stream;

    try {
      await this.buildGraph(context, stream);
    } catch (error) {
      if (generation !== this.generation) return;
      console.error('[audio] failed to build graph', error);
      this.teardown();
      this.fail('no-worklet');
      return;
    }
    if (generation !== this.generation) return;

    // Safari can leave the context suspended despite the resume above; say so
    // rather than sitting on a silent meter forever.
    if (context.state === 'suspended') {
      await context.resume().catch(() => {});
      if (context.state === 'suspended') {
        this.fail('context-suspended');
        return;
      }
    }

    this.watchDevice();
    this.setState({ status: 'calibrating' });

    const calibration = await this.runCalibration();
    if (generation !== this.generation) return;
    if (!calibration) {
      this.fail('unknown');
      return;
    }
    this.setState({ status: 'ready', calibration });
  }

  private async buildGraph(context: AudioContext, stream: MediaStream): Promise<void> {
    await context.audioWorklet.addModule(WORKLET_URL);

    this.source = context.createMediaStreamSource(stream);
    this.node = new AudioWorkletNode(context, 'capture-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: {
        hopSize: CAPTURE.hopSize,
        clipThreshold: CAPTURE.clipThreshold,
      },
    });

    // Some browsers stop pulling on a worklet whose output goes nowhere.
    // A muted gain node keeps the graph alive without any audible output —
    // and without feeding the guitar back through the speakers.
    this.silentSink = context.createGain();
    this.silentSink.gain.value = 0;
    this.source.connect(this.node);
    this.node.connect(this.silentSink);
    this.silentSink.connect(context.destination);

    this.worker = new AnalysisWorker();
    this.worker.onmessage = this.handleWorkerMessage;
    this.worker.onerror = (event) => console.error('[audio] worker error', event.message);

    // Direct audio-thread → worker channel. The main thread is not in the path.
    const channel = new MessageChannel();
    this.postToWorker(
      {
        type: 'init',
        sampleRate: context.sampleRate,
        hopSize: CAPTURE.hopSize,
        sink: channel.port2,
      },
      [channel.port2],
    );
    this.node.port.postMessage({ type: 'sink', port: channel.port1 }, [channel.port1]);

    const track = stream.getAudioTracks()[0];
    this.setState({
      contextSampleRate: context.sampleRate,
      appliedConstraints: track?.getSettings(),
    });
  }

  /** Resolves with the profile, or null if it timed out or was superseded. */
  private runCalibration(): Promise<CalibrationResult | null> {
    return new Promise<CalibrationResult | null>((resolve) => {
      const settle = (result: CalibrationResult | null) => {
        if (this.calibrationSettle !== settle) return;
        this.calibrationSettle = null;
        clearTimeout(timer);
        resolve(result);
      };
      const timer = setTimeout(() => settle(null), (CALIBRATION.durationSec + 4) * 1000);
      this.calibrationSettle = settle;
      this.postToWorker({ type: 'calibrate', durationSec: CALIBRATION.durationSec });
    });
  }

  setTarget(chordId: string | null): void {
    this.postToWorker({ type: 'setTarget', chordId });
  }

  setStringAnalysis(enabled: boolean): void {
    this.postToWorker({ type: 'setStringAnalysis', enabled });
  }

  /** Turn the tuner branch on or off. Off is the default; readings cost CPU. */
  setTuner(enabled: boolean): void {
    this.postToWorker({ type: 'setTuner', enabled });
  }

  resetAnalysis(): void {
    this.postToWorker({ type: 'reset' });
  }

  /**
   * Resume after a tab switch or an iOS screen lock.
   *
   * Safari reports a non-standard `'interrupted'` state after a screen lock or
   * a phone call — it is not `'suspended'`, and a check for `'suspended'` alone
   * quietly leaves the session dead on exactly the platform this path exists
   * for. Anything that is not running and not closed gets a resume attempt.
   */
  async resume(): Promise<void> {
    const context = this.context;
    if (!context) return;
    const state = context.state as string;
    if (state === 'running' || state === 'closed') return;
    try {
      await context.resume();
    } catch (error) {
      console.warn('[audio] resume failed', error);
      this.fail('context-suspended');
    }
  }

  /** Release the microphone and every resource behind it. */
  stop(): void {
    if (!this.context && !this.stream && !this.worker) return;
    this.teardown();
    this.setState({ status: 'idle', level: 0, frame: null, calibration: undefined }, true);
  }

  // ── internals ─────────────────────────────────────────────────────────

  /**
   * Drop every resource without touching the reported status.
   *
   * Bumping the generation first is what makes a retry safe: any getUserMedia
   * or calibration still in flight sees a stale generation and discards itself
   * instead of attaching a second stream, context and worker to the engine.
   */
  private teardown(): void {
    this.generation++;
    this.calibrationSettle?.(null);
    this.calibrationSettle = null;

    try {
      this.node?.port.postMessage({ type: 'stop' });
    } catch {
      // The port may already be closed; nothing to recover.
    }
    this.worker?.terminate();
    this.worker = null;

    try {
      this.source?.disconnect();
      this.node?.disconnect();
      this.silentSink?.disconnect();
    } catch {
      // Nodes may already be detached.
    }
    this.node = null;
    this.source = null;
    this.silentSink = null;

    this.releaseStream();

    const context = this.context;
    this.context = null;
    if (context) void context.close().catch(() => {});
  }

  private handleWorkerMessage = (event: MessageEvent<FromWorker>): void => {
    const message = event.data;
    switch (message.type) {
      case 'onset': {
        for (const listener of this.onsetListeners) listener(message.onset);
        break;
      }
      case 'frame': {
        this.handleFrame(message.frame);
        break;
      }
      case 'pitch': {
        for (const listener of this.pitchListeners) listener(message.pitch);
        break;
      }
      case 'calibrated': {
        const profile = toNoiseProfile(message.profile);
        this.calibrationSettle?.({ profile, quality: gradeCalibration(profile) });
        break;
      }
      case 'error': {
        console.error('[audio] worker reported', message.message);
        break;
      }
      case 'ready':
        break;
    }
  };

  private handleFrame(serialized: SerializableFrame): void {
    const frame: EngineFrame = {
      time: serialized.time,
      chroma: serialized.chroma,
      rms: serialized.rms,
      snrDb: serialized.snrDb,
      tonalness: serialized.tonalness,
      clipped: serialized.clipped,
      ...(serialized.source ? { source: serialized.source } : {}),
      ...(serialized.noteCoverage !== undefined ? { noteCoverage: serialized.noteCoverage } : {}),
      ...(serialized.bass ? { bass: serialized.bass } : {}),
      ...(serialized.strings ? { strings: serialized.strings as StringReport[] } : {}),
    };

    for (const listener of this.frameListeners) listener(frame);

    let level = this.state.level;
    for (let i = 0; i < serialized.levels.length; i++) {
      const target = Math.min(1, serialized.levels[i] * 6);
      level += (target - level) * LEVEL_SMOOTHING;
    }
    this.setState({ level, frame });
  }

  private postToWorker(message: ToWorker, transfer: Transferable[] = []): void {
    this.worker?.postMessage(message, transfer);
  }

  private watchDevice(): void {
    const track = this.stream?.getAudioTracks()[0];
    if (!track) return;
    const generation = this.generation;
    track.onended = () => {
      // A device unplugged mid-session, not our own teardown.
      if (generation !== this.generation) return;
      this.fail('no-device');
    };
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    this.stream = null;
  }

  private fail(errorCode: AudioErrorCode): void {
    this.setState({ status: statusForError(errorCode), errorCode }, true);
  }

  private setState(patch: Partial<AudioEngineState>, immediate = false): void {
    const statusChanged = patch.status !== undefined && patch.status !== this.state.status;
    this.state = { ...this.state, ...patch };
    if (immediate || statusChanged) {
      this.flush();
      return;
    }
    const now = Date.now();
    const elapsed = now - this.lastEmit;
    if (elapsed >= STATE_EMIT_INTERVAL_MS) {
      this.flush();
    } else if (!this.emitTimer) {
      this.emitTimer = setTimeout(() => this.flush(), STATE_EMIT_INTERVAL_MS - elapsed);
    }
  }

  private flush(): void {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
    this.lastEmit = Date.now();
    for (const listener of this.stateListeners) listener();
  }
}

function statusForError(code: AudioErrorCode): MicStatus {
  switch (code) {
    case 'permission-denied':
      return 'denied';
    case 'no-device':
    case 'device-in-use':
      return 'unavailable';
    case 'no-worklet':
    case 'no-getusermedia':
    case 'insecure-context':
      return 'unsupported';
    default:
      return 'error';
  }
}

function detectUnsupported(): AudioErrorCode | null {
  if (typeof window === 'undefined') return 'unknown';
  if (!window.isSecureContext) return 'insecure-context';
  if (!navigator.mediaDevices?.getUserMedia) return 'no-getusermedia';
  if (typeof AudioContext === 'undefined') return 'no-getusermedia';
  if (typeof AudioWorkletNode === 'undefined') return 'no-worklet';
  return null;
}

function mapGetUserMediaError(error: unknown): AudioErrorCode {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'permission-denied';
    case 'NotFoundError':
    case 'OverconstrainedError':
    case 'DevicesNotFoundError':
      return 'no-device';
    case 'NotReadableError':
    case 'AbortError':
    case 'TrackStartError':
      return 'device-in-use';
    default:
      return 'unknown';
  }
}

function gradeCalibration(profile: {
  floorDb: number;
  clipRatio: number;
  headroomDb: number;
}): CalibrationQuality {
  if (profile.clipRatio > CALIBRATION.clipRatioWarn) return 'clipping';
  if (profile.floorDb > CALIBRATION.noiseFloorWarnDb) return 'noisy';
  if (profile.floorDb < CALIBRATION.noiseFloorSilentDb) return 'quiet';
  if (profile.headroomDb < CALIBRATION.minHeadroomDb) return 'clipping';
  return 'good';
}

/** Single instance shared by the whole app. */
export const audioEngine = new AudioEngine();
